import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo } from "react";

import { Ecr } from "api/memberPortal/ecr/ecr.types";
import { useGetAllProjectPpByProductIdQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import { useGetAllTdsByIdQuery } from "api/memberPortal/technicalDecisions/technicalDecisions.api";

/**
 * Hook for fetching cascading PP/TD option data.
 * Returns fetch functions + memoized options for dropdowns.
 * Automatically prefetches PP/TD data in edit mode.
 */
export const useEcrCascadingData = (
  ecr?: Ecr,
  vid?: number | string,
  ppId?: number,
) => {
  const { data: protectionProfiles, isFetching: isFetchingPps } =
    useGetAllProjectPpByProductIdQuery(vid ?? skipToken);

  const { data: tds, isFetching: isFetchingTds } = useGetAllTdsByIdQuery(
    ppId || ecr?.pid.product_id || skipToken,
  );

  const ppOptions = useMemo(() => {
    if (!protectionProfiles) return [];
    return protectionProfiles.map((pp) => ({
      value: pp.pp__pp_id || "",
      label: pp.pp__pp_short_name || "",
    }));
  }, [protectionProfiles]);

  const tdOptions = useMemo(() => {
    if (!tds) return [];
    return tds.map((td) => ({
      label: td.identifier ?? td.title ?? "",
      value: td.td_id,
    }));
  }, [tds]);

  return {
    isLoading: isFetchingPps ?? isFetchingTds,
    ppOptions,
    tdOptions,
  };
};
